import { FixedContainer } from '@calendly/ui/components/fixed-container';
import classNames from 'classnames';
import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useShellCenterPanelCollapsed } from 'features/app_shell/shell_layout/useShellCenterPanelCollapsed';
import { SHELL_CONTENT_SCROLL_ELEMENT_ID } from 'shared/constants/app_shell';
import { MeetingsTabLoader } from './meetings_tab_loader';
const MeetingsTabInitialLoadContext = createContext(null);

/** Legacy portal host id — removed on mount so dev HMR cannot leave a second loader behind. */
const MEETINGS_TAB_LOADER_HOST_ID = 'meetings-tab-loader-host';

/**
 * Tracks chunk / settings / list initial-load phases. One in-tree overlay loader stays
 * centered in the meetings tab shell until every active source has cleared.
 */
export const MeetingsTabInitialLoadProvider = ({
  children
}) => {
  const [loadingSources, setLoadingSources] = useState(() => ({
    chunk: true
  }));
  const setSourceLoading = useCallback((source, isLoading) => {
    setLoadingSources(prev => {
      if (Boolean(prev[source]) === isLoading) return prev;
      const next = {
        ...prev
      };
      if (isLoading) {
        next[source] = true;
      } else {
        delete next[source];
      }
      return next;
    });
  }, []);

  /** Atomically clear chunk and activate settings so the overlay never drops between phases. */
  const handoffFromChunk = useCallback(isSettingsLoading => {
    setLoadingSources(prev => {
      const hadChunk = Boolean(prev.chunk);
      const settingsMatches = Boolean(prev.settings) === isSettingsLoading;
      if (!hadChunk && settingsMatches) return prev;
      const next = {
        ...prev
      };
      delete next.chunk;
      if (isSettingsLoading) {
        next.settings = true;
      } else {
        delete next.settings;
      }
      return next;
    });
  }, []);
  const isInitialLoadActive = Object.keys(loadingSources).length > 0;
  const value = useMemo(() => ({
    isInitialLoadActive,
    handoffFromChunk,
    setSourceLoading
  }), [handoffFromChunk, isInitialLoadActive, setSourceLoading]);
  return <MeetingsTabInitialLoadContext.Provider value={value}>
      {children}
    </MeetingsTabInitialLoadContext.Provider>;
};

/** Register or clear a loading phase (chunk, settings, list). */
export const useMeetingsTabInitialLoad = (source, isActive) => {
  const context = useContext(MeetingsTabInitialLoadContext);
  useLayoutEffect(() => {
    if (!context) return undefined;
    context.setSourceLoading(source, isActive);
    return () => context.setSourceLoading(source, false);
  }, [context, source, isActive]);
};
const useMeetingsTabInitialLoadActive = () => {
  const context = useContext(MeetingsTabInitialLoadContext);
  return context?.isInitialLoadActive ?? false;
};

/** Call once the lazy meetings page module has mounted (chunk download finished). */
export const useMeetingsTabChunkLoaded = () => {
  const context = useContext(MeetingsTabInitialLoadContext);
  useLayoutEffect(() => {
    context?.setSourceLoading('chunk', false);
  }, [context]);
};

/** Hand off chunk → settings on MeetingsPage mount; keeps settings in sync until resolved. */
export const useMeetingsTabPageLoad = isSettingsLoading => {
  const context = useContext(MeetingsTabInitialLoadContext);
  useLayoutEffect(() => {
    if (!context) return undefined;
    context.handoffFromChunk(isSettingsLoading);
    return () => context.setSourceLoading('settings', false);
  }, [context, isSettingsLoading]);
};
const meetingsTabShellCss = "m29tv40";
const meetingsTabContentCss = "m145ap3x";

/** Sized to the visible content viewport via CSS vars set in useMeetingsTabLoaderViewportBounds. */
const meetingsTabLoaderViewportCss = "m11khwgj";

/** Keep chunk/settings/list work mounted but out of layout while the in-flow loader fills the shell. */
const meetingsTabContentHiddenCss = "m2t9fq5";

/** Drop HMR/orphan nodes from the pre-refactor #meetings-tab-loader-host only; not reachable via refs. */
const removeLegacyMeetingsTabLoaderNodes = body => {
  Array.from(body.children).forEach(node => {
    if (node.id === MEETINGS_TAB_LOADER_HOST_ID) {
      node.remove();
    }
  });
};
const getScrollViewport = element => {
  let node = element.parentElement;
  while (node) {
    if (node.id === SHELL_CONTENT_SCROLL_ELEMENT_ID) return node;
    const {
      overflowY
    } = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.clientHeight > 0) return node;
    node = node.parentElement;
  }
  return null;
};
const MEETINGS_TAB_LOADER_VIEWPORT_VARS = ['--meetings-tab-loader-top', '--meetings-tab-loader-left', '--meetings-tab-loader-width', '--meetings-tab-loader-height'];
const clearMeetingsTabLoaderViewportVars = element => {
  MEETINGS_TAB_LOADER_VIEWPORT_VARS.forEach(name => {
    element.style.removeProperty(name);
  });
};

/** Pin loader bounds to the visible Home content slot (product refresh + legacy). */
const useMeetingsTabLoaderViewportBounds = (isLoading, shellRef, overlayRef, isCenterPanelCollapsed) => {
  useLayoutEffect(() => {
    const shell = shellRef.current;
    const overlay = overlayRef.current;
    if (!isLoading || isCenterPanelCollapsed || !shell || !overlay) return undefined;
    const update = () => {
      const homeContent = shell.closest('[data-component="home-content"]');
      const viewport = getScrollViewport(shell);
      const anchorRect = homeContent?.getBoundingClientRect() ?? viewport?.getBoundingClientRect() ?? {
        top: 0,
        left: 0,
        right: window.innerWidth,
        bottom: window.innerHeight
      };
      const viewportRect = viewport?.getBoundingClientRect() ?? {
        top: 0,
        left: 0,
        right: window.innerWidth,
        bottom: window.innerHeight
      };

      // Top: below Home chrome (upgrade banner, etc.). Bottom: full scroll viewport —
      // home-content collapses while loading so its bottom cannot size the overlay.
      const top = Math.max(anchorRect.top, viewportRect.top);
      const bottom = viewportRect.bottom;
      const left = viewportRect.left;
      const right = viewportRect.right;
      overlay.style.setProperty('--meetings-tab-loader-top', `${top}px`);
      overlay.style.setProperty('--meetings-tab-loader-left', `${left}px`);
      overlay.style.setProperty('--meetings-tab-loader-width', `${Math.max(right - left, 0)}px`);
      overlay.style.setProperty('--meetings-tab-loader-height', `${Math.max(bottom - top, 0)}px`);
    };
    update();
    const viewport = getScrollViewport(shell);
    const resizeObserver = viewport ? new ResizeObserver(update) : null;
    resizeObserver?.observe(viewport);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      clearMeetingsTabLoaderViewportVars(overlay);
    };
  }, [isCenterPanelCollapsed, isLoading, overlayRef, shellRef]);
};

/** Hosts meetings tab children; renders exactly one loader overlay while initial load is active. */
export const MeetingsTabInitialLoadShell = ({
  children
}) => {
  const isLoading = useMeetingsTabInitialLoadActive();
  const isCenterPanelCollapsed = useShellCenterPanelCollapsed();
  const shellRef = useRef(null);
  const overlayRef = useRef(null);
  useMeetingsTabLoaderViewportBounds(isLoading, shellRef, overlayRef, isCenterPanelCollapsed);
  useLayoutEffect(() => {
    const body = shellRef.current?.ownerDocument?.body;
    if (body) removeLegacyMeetingsTabLoaderNodes(body);
  }, []);
  return <div ref={shellRef} data-meetings-tab-shell className={meetingsTabShellCss}>
      {isLoading && !isCenterPanelCollapsed && <FixedContainer ref={overlayRef} className={meetingsTabLoaderViewportCss} aria-busy="true">
          <MeetingsTabLoader fill />
        </FixedContainer>}
      <div className={classNames(meetingsTabContentCss, isLoading && meetingsTabContentHiddenCss)} aria-hidden={isLoading}>
        {children}
      </div>
    </div>;
};

require("./meetings_tab_initial_load.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meetings_tab_initial_load.js");